// ============================================================================
// INBOX — la pantalla que el equipo va a tener abierta todo el día (C7-D2).
// Tres columnas en escritorio (lista · hilo · ficha) y UNA sola en móvil:
// lista → hilo → ficha, cada paso con su camino de vuelta.
// Vive en Realtime: un mensaje entrante aparece sin recargar.
// ============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bot, Filter, Search, User, WifiOff } from "lucide-react";
import { Avatar, Chip, SkeletonLista, Vacio } from "./piezas.tsx";
import { Ficha360 } from "./Ficha360.tsx";
import { Hilo } from "./Hilo.tsx";
import {
  crearNota as rpcCrearNota, cerrarConversacion, devolverAlBot, explicarError,
  leerFicha, listarConversaciones, listarEtapas, listarMensajes, listarNotas, listarRespuestasRapidas,
  editarContacto, marcarLeida, moverEtapa as rpcMoverEtapa, reclamarChat, suscribirInbox, tomarChat,
  urlDeMedia,
  adjuntarYEnviar, instruirAlBot, CHAT_DE_OTRA_PERSONA,
} from "./datos.ts";
// ⛔ LA LÓGICA DE ENVIAR NO VIVE AQUÍ (T-LAT-3 · I-6). La comparten este inbox y
// el panel lateral del embudo: dos copias se separan al primer arreglo y el
// síntoma —el bot hablando encima de la asesora— se ve en una venta, no en el CI.
import { responderComoAsesor } from "./responder.ts";
import {
  fechaCorta, laTieneAlguien, nadieLaHaTomado, nombreMostrado, previoDeMensaje, ventanaAbierta,
  type Contacto, type ContextoPanel, type Conversacion, type Etapa, type FichaCalificacion, type Mensaje, type Nota,
  type RespuestaRapida,
} from "./tipos.ts";

type Filtro = "todas" | "bot" | "asesor" | "sin_leer";

const FILTROS: { id: Filtro; texto: string }[] = [
  { id: "todas", texto: "Todas" },
  { id: "sin_leer", texto: "Sin leer" },
  { id: "bot", texto: "Bot" },
  { id: "asesor", texto: "Asesor" },
];

function FilaConversacion({
  c, activa, onClick,
}: { c: Conversacion; activa: boolean; onClick: () => void }) {
  const nombre = nombreMostrado(c.contacto);
  const abierta = ventanaAbierta(c.ventana_expira_en);
  return (
    <button
      onClick={onClick}
      aria-current={activa ? "true" : undefined}
      className={`w-full text-left flex gap-3 px-3 py-3 border-b border-borde-suave/70 cursor-pointer
        transition-colors duration-100 active:bg-tinta/[0.05]
        ${activa ? "bg-marca-suave/60" : "hover:bg-tinta/[0.03]"}`}
    >
      <Avatar nombre={nombre} size={42} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className={`truncate text-[14.5px] ${c.no_leidos > 0 ? "font-semibold" : "font-medium"}`}>{nombre}</span>
          <span className="ml-auto shrink-0 text-[11px] text-tinta-2 cifra">{fechaCorta(c.ultima_actividad)}</span>
        </span>
        <span className="flex items-center gap-1.5 mt-0.5">
          <span className={`truncate text-[13px] ${c.no_leidos > 0 ? "text-tinta" : "text-tinta-2"}`}>
            {c.ultimo_texto || "—"}
          </span>
          {c.no_leidos > 0 && (
            <span className="ml-auto shrink-0 min-w-[19px] h-[19px] px-1 grid place-items-center rounded-full
              bg-marca text-white text-[11px] font-semibold cifra">{c.no_leidos}</span>
          )}
        </span>
        <span className="flex items-center gap-1 mt-1">
          {c.estado === "bot" && <Chip tono="marca"><Bot size={10} /> Bot</Chip>}
          {/* 🔴 «humano» sin dueño = la escaló el bot y NADIE la ha tomado.
              Con el chip verde de «Asesor» el equipo pasaba de largo. */}
          {/* ⭐ T-LAT-7: la regla NO se escribe aquí, se REÚSA (`tipos.ts`). Este
              rótulo se pinta en tres pantallas y llegó a decidirse con dos reglas
              distintas a la vez (H-1 del fiscal, 2-sep-2026). */}
          {nadieLaHaTomado(c.estado, c.asignada_a) && <Chip tono="peligro"><User size={10} /> Esperando asesor</Chip>}
          {laTieneAlguien(c.estado, c.asignada_a) && <Chip tono="ok"><User size={10} /> Asesor</Chip>}
          {c.estado === "cerrada" && <Chip tono="apagado">Cerrada</Chip>}
          {!abierta && c.estado !== "cerrada" && <Chip tono="alerta">24 h vencidas</Chip>}
        </span>
      </span>
    </button>
  );
}

export function Inbox({
  ctx, convInicial,
}: {
  ctx: ContextoPanel;
  /** Conversación a abrir al entrar (p. ej. la que dejó "Probar bot"). */
  convInicial?: string | null;
}) {
  const { sb, tenantId, usuarioId } = ctx;

  const [convs, setConvs] = useState<Conversacion[]>([]);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [respuestas, setRespuestas] = useState<RespuestaRapida[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);

  const [activaId, setActivaId] = useState<string | null>(null);
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [cargandoHilo, setCargandoHilo] = useState(false);
  const [notas, setNotas] = useState<Nota[]>([]);
  // T-CAL-2 · lo que el bot ya anotó del contacto abierto (solo se muestra).
  const [ficha, setFicha] = useState<FichaCalificacion | null>(null);
  const [errorFicha, setErrorFicha] = useState<string | null>(null);

  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [busqueda, setBusqueda] = useState("");
  const [fichaAbierta, setFichaAbierta] = useState(false);

  // ---- T-PAN-5 · el canal en vivo -----------------------------------------
  // `canalCaido` pinta la franja. `huboCaida` recuerda que YA se cayó una vez,
  // y es lo que distingue «volvió» de «acaba de montarse»: sin ese recuerdo, la
  // primera suscripción dispararía una recarga en CADA montaje del inbox.
  const [canalCaido, setCanalCaido] = useState(false);
  const huboCaida = useRef(false);

  const activaRef = useRef<string | null>(null);
  activaRef.current = activaId;

  const activa = useMemo(() => convs.find((c) => c.id === activaId) ?? null, [convs, activaId]);

  // ---- carga inicial ------------------------------------------------------
  const recargar = useCallback(async () => {
    try {
      const [cs, es, rr] = await Promise.all([
        listarConversaciones(sb, tenantId),
        listarEtapas(sb, tenantId),
        listarRespuestasRapidas(sb, tenantId),
      ]);
      setConvs(cs); setEtapas(es); setRespuestas(rr); setErrorCarga(null);
    } catch (e) {
      setErrorCarga(explicarError(e));
    } finally {
      setCargando(false);
    }
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  /** Los mensajes del hilo ABIERTO. `recargar()` trae la lista, las etapas y las
   *  respuestas rápidas, pero NO el hilo: si el asesor tenía una conversación
   *  abierta durante el corte, sin esto se queda mirando un hilo viejo con la
   *  lista ya al día — peor que no reponer nada, porque parece correcto. */
  const recargarHilo = useCallback(async () => {
    const id = activaRef.current;
    if (!id) return;
    try {
      const ms = await listarMensajes(sb, id);
      if (activaRef.current === id) setMensajes(ms);
    } catch { /* el hilo se repone al abrirlo; la franja ya avisó del corte */ }
  }, [sb]);

  // Llegando desde "Probar bot": abrir esa conversación en cuanto esté cargada.
  // Una sola vez por id, para no secuestrar la selección del usuario después.
  const inicialAplicada = useRef<string | null>(null);
  useEffect(() => {
    if (!convInicial || inicialAplicada.current === convInicial) return;
    if (!convs.some((c) => c.id === convInicial)) return;
    inicialAplicada.current = convInicial;
    setActivaId(convInicial);
    setFichaAbierta(false);
  }, [convInicial, convs]);

  // ---- realtime -----------------------------------------------------------
  useEffect(() => {
    return suscribirInbox(sb, tenantId, {
      // ── T-PAN-5 · EL PANEL SORDO SE REPONE, O LO DICE (R-PAN-01) ──────────
      // 🔴 Medido el 1-sep-2026 sobre el panel PUBLICADO, con la red cortada de
      // verdad: el socket muere, la librería lo reconecta sola y el canal se
      // vuelve a suscribir solo (`Subscribed to PostgreSQL` a los 81 s) — pero
      // **no se hace UNA SOLA consulta a la base al volver**. Realtime no repite
      // lo del hueco, así que la asesora sigue viendo una palomita quieta y un
      // chat sin su último mensaje, sin nada que lo delate.
      // ⛔ En la PRIMERA suscripción NO se recarga: sería una consulta doble en
      // cada montaje del inbox. Solo se repone lo que se cayó.
      estado: (e) => {
        if (e === "caido") { huboCaida.current = true; setCanalCaido(true); return; }
        setCanalCaido(false);
        if (!huboCaida.current) return;      // primera suscripción: nada que reponer
        huboCaida.current = false;
        void recargar();
        void recargarHilo();
      },
      mensaje: (m) => {
        // si el hilo abierto es el suyo, pintarlo ya; si no, refrescar la lista
        if (m.conversacion_id === activaRef.current) {
          setMensajes((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
        }
        // ⛔ UN EVENTO DEL SISTEMA NO ES PREVIO (`previoDeMensaje` devuelve null):
        //    se deja el que había. ⚠️ NO se escribe el `null` en `ultimo_texto` —
        //    eso BORRARÍA la línea, que es otro fallo. El hilo de arriba sí lo
        //    pinta: la burbuja del evento se sigue viendo dentro del chat.
        const previo = previoDeMensaje(m);
        if (previo === null) return;
        // ── T-PRE-9 · I-13 · EL PREVIO LO DICE UNA SOLA REGLA ──────────────
        // 🏚️ Aquí decía `String((m.cuerpo as any)?.texto ?? "…")`, y estaba MAL
        // desde el día uno: una **foto sin pie** sale «· foto» en la lista que
        // viene de `v_inbox` y salía **«…»** en cuanto llegaba en vivo. Dos
        // verdades para el mismo mensaje en la misma lista, según se hubiera
        // recargado o no — y «…» no le dice a nadie de qué se está hablando,
        // que es justo lo que se mira para decidir a quién contestar.
        // ⛔ La regla vive en `tipos.ts` y es el gemelo del `case` de la vista.
        //    Aquí NO se decide nada: se aplica.
        setConvs((prev) => prev.map((c) => c.id === m.conversacion_id
          ? { ...c, ultimo_texto: previo, ultima_actividad: m.creado_en }
          : c));
      },
      conversacion: (c) => {
        setConvs((prev) => {
          const i = prev.findIndex((x) => x.id === c.id);
          if (i === -1) { void recargar(); return prev; }   // conversación nueva: traer su contacto
          const fusion = { ...prev[i], ...c } as Conversacion;
          const otras = prev.filter((_, j) => j !== i);
          return [fusion, ...otras].sort((a, b) => +new Date(b.ultima_actividad) - +new Date(a.ultima_actividad));
        });
      },
      // ── T-PAN · LA PALOMITA SE MUEVE SOLA ────────────────────────────────
      // 🔴 El síntoma que cierra esto (Cristhian, 31-ago-2026, tres veces):
      // «las palomitas no se actualizan; para verlas tengo que recargar la
      // página o cambiar de conversación y volver».
      //
      // ⚠️ SE PINTA LO QUE DICE LA BASE, TAL CUAL — sin lógica de «un acuse no
      // puede retroceder». WhatsApp entrega statuses fuera de orden y
      // `actualizar_entrega` (0003:186) se queda con EL ÚLTIMO que llegó: si la
      // pantalla decidiera por su cuenta que `leido` no baja a `entregado`,
      // enseñaría algo distinto de lo que se ve al recargar, y el asesor tendría
      // dos verdades. El invariante es «el panel dice lo mismo que un F5», y esa
      // regla —si hay que cambiarla— se cambia en la base, no aquí.
      entrega: (a) => {
        setMensajes((prev) => {
          const i = prev.findIndex((m) => m.id === a.mensaje_id);
          // No es del hilo abierto (o el hilo aún no cargó): devolver el MISMO
          // arreglo, no una copia. Un arreglo nuevo re-renderiza el hilo entero
          // por cada acuse de cualquier conversación del negocio.
          if (i === -1 || prev[i].entrega === a.estado) return prev;
          const copia = prev.slice();
          copia[i] = { ...copia[i], entrega: a.estado };
          return copia;
        });
      },
      // ── T-PAN · EL ADJUNTO QUE NO SALIÓ SE VE QUE NO SALIÓ (R-ADJ-01) ────
      // El chip «no se pudo enviar» lo pinta `Hilo.tsx` leyendo `media.estado`.
      // Ese `fallido` lo escribe el WORKER, minutos después de que el asesor
      // soltó el archivo: sin esto, la señal que costó 24 días de silencio
      // existe en la base y no llega a la pantalla hasta que alguien recargue.
      media: (a) => {
        setMensajes((prev) => {
          const i = prev.findIndex((m) => m.id === a.mensaje_id);
          if (i === -1) return prev;
          const { mensaje_id: _ignorado, ...campos } = a;
          const antes = prev[i].media;
          // Igual que arriba: si no cambia nada, no se toca el estado.
          if (antes && (Object.keys(campos) as (keyof typeof campos)[])
            .every((k) => antes[k] === campos[k])) return prev;
          const copia = prev.slice();
          copia[i] = { ...copia[i], media: campos };
          return copia;
        });
      },
    });
  }, [sb, tenantId, recargar, recargarHilo]);

  // ---- hilo activo --------------------------------------------------------
  useEffect(() => {
    if (!activaId) { setMensajes([]); setNotas([]); return; }
    let vivo = true;
    setCargandoHilo(true);
    (async () => {
      try {
        const ms = await listarMensajes(sb, activaId);
        if (!vivo) return;
        setMensajes(ms);
        const conv = convs.find((c) => c.id === activaId);
        if (conv?.no_leidos) {
          await marcarLeida(sb, activaId);
          setConvs((prev) => prev.map((c) => (c.id === activaId ? { ...c, no_leidos: 0 } : c)));
        }
        if (conv?.contacto_id) {
          const ns = await listarNotas(sb, conv.contacto_id);
          if (vivo) setNotas(ns);
        }
      } catch (e) {
        if (vivo) setErrorCarga(explicarError(e));
      } finally {
        if (vivo) setCargandoHilo(false);
      }
    })();
    return () => { vivo = false; };
    // `convs` a propósito fuera: solo re-cargamos al cambiar de conversación.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activaId, sb]);

  // ---- T-CAL-2 · la ficha de calificación del contacto abierto --------------
  // ⛔ I-94 · VA APARTE del efecto del hilo, con su propio `catch`: dentro de ese
  // `try`, un fallo al leer la ficha saltaría al cartel rojo de `errorCarga` y
  // cortaría la carga de las notas. Aquí un fallo solo deja un aviso discreto en
  // la ficha. Se lee al abrir el contacto (el realtime NO escucha `contactos`).
  const contactoActivoId = convs.find((c) => c.id === activaId)?.contacto_id ?? null;
  useEffect(() => {
    setFicha(null); setErrorFicha(null);
    if (!contactoActivoId) return;
    let vivo = true;
    leerFicha(sb, tenantId, contactoActivoId).then(
      (f) => { if (vivo) setFicha(f); },
      () => { if (vivo) setErrorFicha("No se pudo cargar lo que sabemos del cliente."); },
    );
    return () => { vivo = false; };
  }, [contactoActivoId, sb, tenantId]);

  // ---- acciones -----------------------------------------------------------
  /**
   * C2-D4: URL temporal para oír/ver lo que mandó el cliente.
   * ⚠️ `useCallback` NO es optimización: `MediaDelCliente` la tiene en las
   * dependencias de su efecto. Sin memorizar, cada render crearía una función
   * nueva, el efecto se volvería a disparar y el panel pediría URLs firmadas
   * en bucle — un bucle que no se ve, solo se paga.
   */
  const firmarMedia = useCallback((ruta: string) => urlDeMedia(sb, ruta), [sb]);

  /** T-ETQ-4 (R-ETQ-01): título, nombre del equipo y etiquetas por `editar_contacto`;
   *  la lista se parcha al instante (el nombre de la fila cambia con el del equipo). */
  async function editarContactoActivo(c: { nombre_equipo: string | null; titulo: string | null; etiquetas: string[] }) {
    if (!activa) return;
    try { await editarContacto(sb, activa.contacto_id, c); }
    catch (e) { throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) }); }
    setConvs((p) => p.map((x) => (x.contacto_id === activa.contacto_id && x.contacto
      ? { ...x, contacto: { ...x.contacto, ...c } } : x)));
  }

  const conError = (fn: () => Promise<void>) => async () => {
    try { await fn(); } catch (e) { setErrorCarga(explicarError(e)); }
  };

  async function enviar(texto: string) {
    if (!activa) return;
    const conv = activa;
    // ⛔ TODA la lógica (pase al humano fuera del `try`, reclamo dentro,
    // `responder_asesor`) vive en `responder.ts` y es LA MISMA que usa el panel
    // lateral del embudo. Aquí solo se dice cómo se parcha la lista.
    await responderComoAsesor(sb, conv, texto, usuarioId, (parche) =>
      setConvs((p) => p.map((c) => (c.id === conv.id ? { ...c, ...parche } : c))));
    try {
      setMensajes(await listarMensajes(sb, conv.id));
    } catch (e: any) {
      throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) });
    }
  }

  const listaFiltrada = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return convs.filter((c) => {
      if (filtro === "bot" && c.estado !== "bot") return false;
      if (filtro === "asesor" && c.estado !== "humano") return false;
      if (filtro === "sin_leer" && !c.no_leidos) return false;
      if (!q) return true;
      const n = nombreMostrado(c.contacto).toLowerCase();
      return n.includes(q) || (c.contacto?.telefono_e164 ?? "").includes(q) || (c.contacto?.wa_id ?? "").includes(q);
    });
  }, [convs, filtro, busqueda]);

  const sinLeer = convs.reduce((s, c) => s + (c.no_leidos > 0 ? 1 : 0), 0);

  return (
    <div className="h-full flex flex-col min-w-0">
      {/* ── T-PAN-5 · LA FRANJA DEL CANAL CAÍDO (R-PAN-01) ────────────────────
          Mismo patrón que `FranjaAlertas` en su rama de fallo: arriba de todo,
          discreta, y **se va sola** cuando el canal vuelve. Una franja que se
          queda pegada enseña a ignorarla, y entonces ya no avisa de nada.
          ⚠️ Lo que esta franja NO puede prometer, y está MEDIDO: durante los
          primeros ~40 s de un corte el canal se declara sano y ya está sordo (el
          socket no se entera hasta que le falla el latido). Avisa cuando el
          canal SE DECLARA caído, no siempre que el panel esté sordo. */}
      {canalCaido && (
        <div role="status" aria-live="polite" data-franja="canal"
          className="shrink-0 flex items-center gap-2 px-3 md:px-4 py-2 text-[13px] leading-snug
            border-b border-borde bg-tenue text-tinta-2">
          <WifiOff size={15} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
          <span>Sin conexión con el servidor. Reintentando; lo que llegue mientras tanto se pondrá al día solo.</span>
        </div>
      )}
      {/* ⛔⛔ `min-h-0` NO ES DECORACIÓN (1-sep-2026, roto en producción ~20 min).
          Un hijo de un contenedor flex nace con `min-height: auto`, o sea NO SE
          DEJA ENCOGER por debajo de su contenido. Sin esto, el área con scroll
          del hilo (`flex-1 overflow-y-auto`, abajo) no recibe altura acotada:
          en vez de desplazarse, CRECE y desborda — y al asesor le queda la
          pantalla congelada, sin poder subir ni bajar dentro del chat.
          Lo introduje yo al envolver las columnas para meter la franja de
          T-PAN-5. Ninguna vara lo cazó y no podía: jsdom no calcula layout. */}
      <div className="flex-1 min-h-0 flex min-w-0">
      {/* ── Columna 1: lista ── (móvil: se esconde cuando hay hilo abierto) */}
      <div className={`${activaId ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[320px] xl:w-[360px]
        2xl:w-[400px] 3xl:w-[460px] shrink-0 border-r border-borde-suave bg-superficie min-w-0`}>
        <div className="shrink-0 px-3 pt-3 pb-2 space-y-2.5 border-b border-borde-suave">
          <div className="flex items-baseline gap-2">
            <h1 className="text-[19px] font-semibold tracking-tight">Conversaciones</h1>
            {sinLeer > 0 && <span className="text-[12px] text-acento font-medium cifra">{sinLeer} sin leer</span>}
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-tinta-2 pointer-events-none" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              type="search"
              placeholder="Buscar por nombre o número"
              aria-label="Buscar conversación"
              className="w-full h-11 md:h-9 pl-9 pr-3 rounded-lg bg-elevado border border-borde text-sm
                placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto scroll-fino -mx-0.5 px-0.5 pb-0.5" role="tablist" aria-label="Filtros">
            {FILTROS.map((f) => (
              <button key={f.id} role="tab" aria-selected={filtro === f.id} onClick={() => setFiltro(f.id)}
                className={`shrink-0 h-8 px-3 rounded-full text-[12.5px] font-medium cursor-pointer
                  transition-colors active:scale-[0.97] ${filtro === f.id
                    ? "bg-tinta text-[var(--color-papel)]"
                    : "bg-elevado text-tinta-2 border border-borde hover:text-tinta"}`}>
                {f.id === "todas" && <Filter size={11} className="inline mr-1 -mt-px" />}{f.texto}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-fino">
          {cargando && <SkeletonLista />}
          {!cargando && listaFiltrada.length === 0 && (
            <Vacio titulo={convs.length ? "Nada con ese filtro" : "Todavía no hay conversaciones"}>
              {convs.length
                ? "Prueba con otro filtro o borra la búsqueda."
                : "Cuando un cliente escriba al WhatsApp del negocio, aparecerá aquí."}
            </Vacio>
          )}
          {listaFiltrada.map((c) => (
            <FilaConversacion key={c.id} c={c} activa={c.id === activaId}
              onClick={() => { setActivaId(c.id); setFichaAbierta(false); }} />
          ))}
        </div>
      </div>

      {/* ── Columna 2: hilo ──
          ⚠️ `onTomar` y `onReclamar` van SIN callback cuando no hay `usuarioId`:
          así el botón no se dibuja y `Hilo` no necesita saber de identidades.
          En estos dos caminos el error SÍ se muestra —al contrario que al
          escribir—: aquí el asesor está pidiendo explícitamente el chat. */}
      <div className={`${activaId ? "flex" : "hidden lg:flex"} flex-1 min-w-0`}>
        <div className="flex-1 min-w-0">
          <Hilo
            firmarMedia={firmarMedia}
            onAdjuntar={activa ? (async (archivo) => {
              // 🔴 `activa` está CONGELADA en el cierre de este render. Sin
              // comparar contra `activaRef.current`, un adjunto que termina
              // después de que el asesor cambió de chat pinta los mensajes de A
              // debajo de la cabecera de B — el mismo hilo con dos historiales.
              const idAlEmpezar = activa.id;
              await adjuntarYEnviar(sb, {
                convId: activa.id, tenantId: activa.tenant_id,
                archivo, autorId: usuarioId ?? null,
              });
              // Se recarga el hilo para que el adjunto aparezca de una: el
              // Realtime del inbox trae los INSERT, pero esperar a que llegue
              // deja al asesor dudando de si el archivo salió.
              // ⚠️ Dos guardas y las dos hacen falta: la de antes de pedir (no
              // gastar la consulta) y la de después (la respuesta puede llegar
              // cuando ya se cambió de chat).
              if (activaRef.current !== idAlEmpezar) return;
              const ms = await listarMensajes(sb, idAlEmpezar);
              if (activaRef.current !== idAlEmpezar) return;
              setMensajes(ms);
            }) : undefined}
            conversacion={activa}
            mensajes={mensajes}
            cargando={cargandoHilo}
            respuestas={respuestas}
            onEnviar={enviar}
            onTomar={usuarioId ? conError(async () => {
              await tomarChat(sb, activa!.id, usuarioId);
              setConvs((p) => p.map((c) => (c.id === activa!.id ? { ...c, estado: "humano", asignada_a: usuarioId } : c)));
              setMensajes(await listarMensajes(sb, activa!.id));
            }) : undefined}
            onReclamar={usuarioId ? conError(async () => {
              await reclamarChat(sb, activa!.id, usuarioId);
              setConvs((p) => p.map((c) => (c.id === activa!.id ? { ...c, asignada_a: usuarioId } : c)));
            }) : undefined}
            onDevolver={conError(async () => {
              await devolverAlBot(sb, activa!.id);
              setConvs((p) => p.map((c) => (c.id === activa!.id ? { ...c, estado: "bot" } : c)));
            })}
            // ⭐ T-HIB-1 · la instrucción al bot. El error NO va a `conError`: lo
            // pinta la propia caja, donde el asesor lo escribió.
            // ⭐ Fiscal H6 (decisión de Cristhian): si el chat lo tiene OTRA
            // persona no hay caja, hay aviso. La base lo rechaza igual (0066).
            avisoInstruccion={activa?.asignada_a && activa.asignada_a !== usuarioId
              ? CHAT_DE_OTRA_PERSONA : undefined}
            onInstruir={activa?.asignada_a && activa.asignada_a !== usuarioId ? undefined : async (texto) => {
              const conv = activa!;
              try { await instruirAlBot(sb, conv.id, texto); }
              catch (e) { throw Object.assign(new Error(explicarError(e)), { mensaje: explicarError(e) }); }
              // La base ya devolvió el chat al bot (0066): la lista lo dice YA.
              setConvs((p) => p.map((c) => (c.id === conv.id
                ? { ...c, estado: "bot", asignada_a: null, motivo_humano: null } : c)));
              if (activaRef.current === conv.id) setMensajes(await listarMensajes(sb, conv.id));
            }}
            onCerrar={conError(async () => {
              await cerrarConversacion(sb, activa!.id);
              setConvs((p) => p.map((c) => (c.id === activa!.id ? { ...c, estado: "cerrada" } : c)));
            })}
            onVolver={() => setActivaId(null)}
            onVerFicha={() => setFichaAbierta(true)}
          />
        </div>
      </div>

      {/* ── Columna 3: ficha ── (xl fija; abajo, panel deslizante desde la derecha) */}
      {activa && (
        <div className="hidden xl:block w-[320px] 2xl:w-[360px] 3xl:w-[420px] shrink-0">
          <Ficha360
            contacto={activa.contacto ?? null}
            etapas={etapas}
            notas={notas}
            ficha={ficha}
            errorFicha={errorFicha}
            onMoverEtapa={async (etapaId) => {
              await rpcMoverEtapa(sb, activa.contacto_id, etapaId);
              setConvs((p) => p.map((c) => (c.id === activa.id && c.contacto
                ? { ...c, contacto: { ...c.contacto, etapa_id: etapaId } as Contacto } : c)));
            }}
            onCrearNota={async (texto) => {
              await rpcCrearNota(sb, activa.contacto_id, texto);
              setNotas(await listarNotas(sb, activa.contacto_id));
            }}
            onEditarContacto={editarContactoActivo}
          />
        </div>
      )}

      {fichaAbierta && activa && (
        <div className="xl:hidden fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Ficha del contacto">
          <button className="flex-1 bg-black/40 cursor-pointer" aria-label="Cerrar ficha"
            onClick={() => setFichaAbierta(false)} />
          <div className="entra w-[86%] max-w-[340px] shadow-[var(--shadow-flotante)] relative">
            <Ficha360
              contacto={activa.contacto ?? null}
              etapas={etapas}
              notas={notas}
              ficha={ficha}
              errorFicha={errorFicha}
              onCerrar={() => setFichaAbierta(false)}
              onMoverEtapa={async (etapaId) => {
                await rpcMoverEtapa(sb, activa.contacto_id, etapaId);
                setConvs((p) => p.map((c) => (c.id === activa.id && c.contacto
                  ? { ...c, contacto: { ...c.contacto, etapa_id: etapaId } as Contacto } : c)));
              }}
              onCrearNota={async (texto) => {
                await rpcCrearNota(sb, activa.contacto_id, texto);
                setNotas(await listarNotas(sb, activa.contacto_id));
              }}
              onEditarContacto={editarContactoActivo}
            />
          </div>
        </div>
      )}

      {errorCarga && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 entra">
          <div role="alert" className="bg-peligro text-white text-[13px] px-4 py-2.5 rounded-lg shadow-[var(--shadow-flotante)] max-w-[90vw]">
            {errorCarga}
            <button onClick={() => setErrorCarga(null)} className="ml-3 underline cursor-pointer">cerrar</button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
